import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
    Box, 
    Table, 
    TableBody, 
    TableContainer, 
    TableRow, 
    Typography, 
    IconButton, 
    TextField,
    InputAdornment,
    Switch,
    TablePagination,
    Pagination,
    Card,
    Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import { useTableState } from '../../../../hooks/useTableState';
import { corregirTexto, eachWordUpper, firstLetterUpperCase, formatDateToYmdNumeric, hasPemissions, usarFuncionesComposicion } from '../../../../commons/utils';
import { ModalConfirmDeactivation } from '../../../../components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from '../../../../components/modals/ModalConfirmRestore';
import { StyledTableCell, StyledTableRow } from '../../../../commons/generics/StyledTableComponents';
import { StatusChip } from '../../../../commons/generics/StatusChip';
import { ClampedText } from '../../../../commons/generics/ClampedText';
import { GenericTableHead, HeadCell } from '../../../../commons/generics/GenericTableHead';
import { SinDatos } from '../../../../commons/generics/SinDatos';

import { FormLinksEncuestasModal } from './FormLinksEncuestasModal';
import { mapLinksEncuestasToTable } from '../mappers/linksEncuestasMapper';
import { 
    startGetLinksEncuestas, 
    startGetLinksEncuestasBySearch,
    startGetLinkEncuestaDetail, 
    startDesactiveLinkEncuesta, 
    startActiveLinkEncuesta 
} from '../../../../store/links_encuestas';

export const TableLinksEncuestas = () => {
    const dispatch = useDispatch();
    const { links_encuestas_list, links_encuestas_detail } = useSelector((state: any) => state.linksEncuestas);

    const {
        dataTableList,
        canView, setCanView,
        canEdit, setCanEdit,
        canDelete, setCanDelete,
        canRestore, setCanRestore,
        openElem, setOpenElem,
        checked, setChecked,
        searchTerm, setSearchTerm,
        order,
        orderBy,
        handleSwitchChange,
        handleSearch: handleSearchHook,
        handleResetSearch: handleResetSearchHook,
        page,
        rowsPerPage,
        handleChangePage,
        handleChangeRowsPerPage,
        pagePagination,
        itemCountPagination,
        changePaginationPage: changePaginationPageHook,
        handleRequestSort,
        updatePaginationFromList,
        openModalDeactivation, setOpenModalDeactivation,
        openModalRestore, setOpenModalRestore,
        ResponseComponents,
        handleResponse,
        getSortedAndFilteredList,
        isServerSide
    } = useTableState('qiratingslinks');

    const [hasPermission, setHasPermission] = useState<boolean>(true);
    const [openModalForm, setOpenModalForm] = useState(false);
    const [selectedLink, setSelectedLink] = useState<any>(null);
    const [isViewMode, setIsViewMode] = useState(false);
    const [inputValue, setInputValue] = useState('');

    // Update mapped list whenever data from backend changes
    // useEffect(() => {
    //     const mappedList = mapLinksEncuestasToTable(links_encuestas_list || []);
    //     updatePaginationFromList(mappedList);
    // }, [links_encuestas_list]);

    useEffect(() => {
        if (links_encuestas_list) {
            updatePaginationFromList(links_encuestas_list);
        }
    }, [links_encuestas_list]);

    const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
        changePaginationPageHook(event, value);
        const response = await dispatch(startGetLinksEncuestas(value, checked ? 0 : 1) as any);
        await handleResponse(response, 'listar links', false);
    };

    const handleSearch = async (param: string) => {
        setSearchTerm(param);
        setInputValue(param);
        handleSearchHook(param, dispatch, (p: string) => startGetLinksEncuestasBySearch(p, 1, checked ? 0 : 1), getInitialData, null, 'buscar links');
    };

    const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        handleSwitchChange(e);
        const newChecked = e.target.checked;
        const status = newChecked ? 0 : 1;
        if (searchTerm) {
            handleSearchHook(searchTerm, dispatch, (p: string) => startGetLinksEncuestasBySearch(p, 1, status), () => getInitialData(status), null, 'buscar links');
        } else {
            getInitialData(status);
        }
    };

    const onResetSearch = () => {
        setInputValue('');
        handleResetSearchHook(() => getInitialData());
    };

    useEffect(() => {
        checkPermissions();
        getInitialData();
    }, []);

    const getInitialData = async (currentStatus?: number) => {
        const status = currentStatus !== undefined ? currentStatus : (checked ? 0 : 1);
        const response = await dispatch(startGetLinksEncuestas(1, status));
        await handleResponse(response, 'obtener links', false);
    };


    const checkPermissions = async () => {
        const view = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.links.encuestas', 'view');
        if (!view) setHasPermission(false);
        else setCanView(true);

        const edit = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.links.encuestas', 'edit');
        if (edit) setCanEdit(true);

        const del = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.links.encuestas', 'delete');
        if (del) setCanDelete(true);

        const rest = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.links.encuestas', 'restore');
        if (rest) setCanRestore(true);
    };

    const handleView = async (link: any) => {
        const response = await dispatch(startGetLinkEncuestaDetail(link.id));
        await handleResponse(response, 'obtener detalle', false);
        if (response?.ok) {
            setSelectedLink(response.data);
            setIsViewMode(true);
            setOpenModalForm(true);
        }
    };

    const handleEdit = async (link: any) => {
        const response = await dispatch(startGetLinkEncuestaDetail(link.id));
        await handleResponse(response, 'obtener detalle', false);
        if (response?.ok) {
            setSelectedLink(response.data);
            setIsViewMode(false);
            setOpenModalForm(true);
        }
    };

    const handleDelete = (link: any) => {
        setOpenElem(link);
        setOpenModalDeactivation(true);
    };

    const handleRestore = (link: any) => {
        setOpenElem(link);
        setOpenModalRestore(true);
    };

    const confirmDeactivate = async () => {
        if (!openElem) return;
        const response = await dispatch(startDesactiveLinkEncuesta(openElem.id, inputValue, pagePagination, checked ? 0 : 1));
        await handleResponse(response, 'desactivar link', true);
    };

    const confirmRestore = async () => {
        if (!openElem) return;
        const response = await dispatch(startActiveLinkEncuesta(openElem.id, inputValue, pagePagination, checked ? 0 : 1));
        await handleResponse(response, 'restaurar link', true);
    };

    const handleCloseForm = () => {
        setOpenModalForm(false);
        setSelectedLink(null);
        setIsViewMode(false);
        getInitialData(); // Refresh list after close
    };

    const handleCopyToken = (row: any) => {
        if (row.shareMessage) {
            navigator.clipboard.writeText(row.shareMessage);
        } else {
            navigator.clipboard.writeText(row.token);
        }
    };

    const handleShareWhatsApp = (row: any) => {
        const content = row.shareMessage ? row.shareMessage : row.token;
        const encodedContent = encodeURIComponent(content);
        window.open(`https://wa.me/?text=${encodedContent}`, '_blank');
    };

    if (!hasPermission) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="error">No tienes permisos para ver este contenido.</Typography>
            </Box>
        );
    }

    const sortedList = getSortedAndFilteredList(
        dataTableList || [], 
        ['name', 'description'], 
        'status', 
        1 // active status value
    );

    const headCells: HeadCell[] = [
        { id: 'name', label: 'Nombre', sortable: true },
        { id: 'survey', label: 'Encuesta', sortable: true },
        { id: 'user', label: 'Usuario', sortable: true },
        { id: 'description', label: 'Descripción', sortable: true },
        { id: 'token', label: 'Url / Link', sortable: false },
        { id: 'createdAt', label: 'Creado El', sortable: true },
        { id: 'responseAt', label: 'Respuesta', sortable: true },
        { id: 'status', label: 'Estado', sortable: true },
        { id: 'actions', label: 'Acciones', sortable: false, align: 'center' },
    ];

    return (
        <Box sx={{ width: '100%', mt: 5 }}>
            {ResponseComponents}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                <TextField
                    placeholder="Buscar link..."
                    variant="outlined"
                    size="small"
                    autoComplete='off'
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    sx={{ width: 400 }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                        endAdornment: inputValue && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={onResetSearch}><ClearIcon /></IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <Switch checked={checked} onChange={onSwitchChange} color="primary" />
                    <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
                        Mostrar solo inactivas (Encuestas)
                    </Typography>
                </Box>  
            </Box>

            <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', overflow: 'hidden' }}>
                <TableContainer>
                <Table>
                    <GenericTableHead
                        headCells={headCells}
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                        {sortedList.length === 0 ? (
                            <TableRow>
                                <StyledTableCell colSpan={9} align="center">
                                    <SinDatos />
                                </StyledTableCell>
                            </TableRow>
                        ) : (
                            sortedList
                            .slice(isServerSide ? 0 : page * rowsPerPage, isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage)
                            .map((row: any) => (
                                <StyledTableRow key={row.id} hover>
                                    <StyledTableCell>
                                        <ClampedText text={usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, row.name)} />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Typography variant="body2" color={!row.survey ? 'text.secondary' : 'textPrimary'}>
                                            {row.survey ? row.survey : 'Sin datos'}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Typography variant="body2" color={!row.user ? 'text.secondary' : 'textPrimary'}>
                                            {row.user ? eachWordUpper(row.user) : 'Sin datos'}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <ClampedText text={usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, row.description)} />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {row.token}
                                            </Typography>
                                            <Tooltip title={row.responseAt ? "Encuesta ya respondida" : "Copiar Token"}>
                                                <span style={{ cursor: row.responseAt ? 'not-allowed' : 'pointer' }}>
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => !row.responseAt && handleCopyToken(row)}
                                                        disabled={!!row.responseAt}
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={row.responseAt ? "Encuesta ya respondida" : "Compartir en WhatsApp"}>
                                                <span style={{ cursor: row.responseAt ? 'not-allowed' : 'pointer' }}>
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => !row.responseAt && handleShareWhatsApp(row)}
                                                        disabled={!!row.responseAt}
                                                        color="success"
                                                    >
                                                        <WhatsAppIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        { formatDateToYmdNumeric(row.createdAt)}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Typography variant="body2" color={!row.responseAt ? 'error' : 'textPrimary'}>
                                            {row.responseAt ? formatDateToYmdNumeric(row.responseAt) : 'Sin contestar'}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <StatusChip status={row.status} />
                                    </StyledTableCell>
                                    <StyledTableCell align="center"> 
                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                            <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => canView && handleView(row)} 
                                                    disabled={!canView}
                                                    color="info"
                                                >
                                                    <VisibilityOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                            
                                            <span style={{ cursor: !canEdit || row.status !== 1 ? 'not-allowed' : 'pointer' }}>
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => canEdit && row.status === 1 && handleEdit(row)} 
                                                    disabled={!canEdit || row.status !== 1}
                                                    color="primary"
                                                >
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                            
                                            {row.status === 1 ? (
                                                <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => canDelete && handleDelete(row)} 
                                                        disabled={!canDelete}
                                                        color="error"
                                                    >
                                                        <DeleteOutlineOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            ) : (
                                                <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => canRestore && handleRestore(row)} 
                                                        disabled={!canRestore}
                                                        color="success"
                                                    >
                                                        <RestoreOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            )}
                                        </Box>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                {dataTableList.length > 0 && (
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={dataTableList.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Filas por página"
                            />
                )}

                 {dataTableList.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, padding: 2.5, borderTop: 1, borderColor: 'divider' }}>
                    <Pagination
                        sx={{ justifyContent: 'center' }}
                        color="primary"
                        count={itemCountPagination}
                        page={pagePagination}
                         onChange={changePaginationPage} 
                        showFirstButton 
                        showLastButton
                        shape="circular"
                    />
                </Box>
            )}

            </TableContainer>
            </Card>

            <FormLinksEncuestasModal 
                open={openModalForm}
                onClose={handleCloseForm}
                isEdit={!isViewMode && selectedLink !== null}
                editData={selectedLink}
                isViewMode={isViewMode}
            />

            <ModalConfirmDeactivation 
                open={openModalDeactivation} 
                setOpen={setOpenModalDeactivation} 
                onConfirm={confirmDeactivate} 
            />
            <ModalConfirmRestore 
                open={openModalRestore} 
                setOpen={setOpenModalRestore} 
                onConfirm={confirmRestore} 
            />
        </Box>
    );
};
